// V2-only Address Change section, rendered inside the "What changed?" drawer.
//
// Mirrors AutomobilePolicy's V2 AddressChangeInformationV2 (see
// AutomobilePolicy/AutomobilePolicyChangeRequest/v2/components/AddressChangeInformationV2.tsx),
// adapted to Habitational (no vehicles/drivers — risk locations are the
// comparable re-rated entity):
//   - "Change" checkbox + explanatory copy about the address/re-rate.
//   - Pill selector for what's changing: Mailing only / Risk location only /
//     Mailing and risk location.
//   - "Change address on customer screen?" Yes/No (reuses the existing
//     sibling field `change_mailing_address_on_customer`).
//   - Address fields (address, unit, city, province, postal code, phone,
//     email).
//   - Active-risk-location list (from the policy) with a checkbox per risk
//     location and CURRENT vs NEW rows for occupancy type, access type,
//     smokers and number of families. Only risk locations the user checks
//     get a NEW row saved to `address_change.risk_location_updates`.
//
// Kept separate from the shared V1 AddressChangeInformation step so V1 is
// unaffected.

import React from "react";
import {
  Box,
  FormControl,
  FormControlLabel,
  Grid,
  Radio,
  RadioGroup,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import produce from "immer";
import {
  ControlledCustomSelect,
  CustomFormLabel,
  CustomTextField,
} from "../../../../../../../../components/formsComponents";
import { CustomCheckbox } from "../../../../../../../../components/formsComponents/CustomCheckbox";
import { AddressSearchAutoComplete } from "../../../../../../../../components/AddressAutoComplete/AddressSearchAutoComplete";
import { ILocationResponsePayload } from "../../../../../../../../components/LocationAutoComplete/LocationAutoComplete";
import { provincesAndStates } from "../../../../../../../../constants/constants";
import { IHabitationalPolicyChangeProps } from "../../Steps/HabitationalPolicyChange.types";
import { useHabitationalPolicyRiskLocation } from "../../../hooks/useHabitationalPolicyRiskLocation";
import { IHabitationalAddressChangeRiskLocationUpdate } from "../../../../../../../../redux/habitationalPolicy/habitationalPolicyChangeRequest/habitationalPolicyChangeReq.types";
import { IHabitationalRiskLocation } from "../../../../../../../../redux/habitationalPolicy/habitationalRiskLocation/habitationalRiskLocation.types";

const ADDRESS_CHANGE_TYPE_OPTIONS = [
  "Mailing only",
  "Risk location only",
  "Mailing and risk location",
] as const;

// Starter options for the per-risk-location re-rate pickers, rendered with
// ControlledCustomSelect (the shared dropdown used everywhere else in this
// panel, e.g. Province).
const OCCUPANCY_TYPE_OPTIONS = [
  "Owner Occupied",
  "Tenant Occupied",
  "Seasonal",
  "Vacant",
].map((label) => ({ label, value: label }));
const ACCESS_TYPE_OPTIONS = ["Owned", "Rented", "Leased"].map((label) => ({
  label,
  value: label,
}));
const SMOKERS_OPTIONS = ["Yes", "No"].map((label) => ({
  label,
  value: label,
}));
const FAMILIES_OPTIONS = ["1", "2", "3", "4", "5+"].map((label) => ({
  label,
  value: label,
}));

// This panel renders inside a RightPanel with zIndex 1400 (see
// ChangeSectionDrawer). MUI's Select opens its menu in a Popover portalled at
// the document root with the default modal z-index (1300), so without this
// override every dropdown menu in this panel renders behind the drawer and
// its options appear "missing" (the menu is open, just hidden underneath).
const SELECT_MENU_PROPS = { sx: { zIndex: 1500 } };

// Read-only "CURRENT" value shown above each editable "NEW" field.
const CurrentValue: React.FC<{ label: string; value: React.ReactNode }> = ({
  label,
  value,
}) => (
  <Box>
    <Typography
      variant="caption"
      color="text.secondary"
      sx={{ display: "block", textTransform: "uppercase", fontSize: "0.68rem" }}
    >
      {label}
    </Typography>
    <Typography variant="body2" fontWeight={600}>
      {value || "--"}
    </Typography>
  </Box>
);

export const AddressChangeInformationV2: React.FC<
  IHabitationalPolicyChangeProps
> = ({ values, setValues, handleChange, customerPolicyId }) => {
  const { riskLocations } = useHabitationalPolicyRiskLocation(customerPolicyId);

  // Active risk locations only, per requirement — the hook already requests
  // the "ACTIVE" status server-side, but we defensively filter here too in
  // case the store still holds a stale/mixed list.
  const activeRiskLocations = React.useMemo(
    () =>
      riskLocations.filter(
        (location: IHabitationalRiskLocation) => location.status === "ACTIVE"
      ),
    [riskLocations]
  );

  const addressChange = values.address_change;

  // `risk_location_updates` was added after some existing change-request
  // records were created, so records loaded from the API may not have it at
  // all. Guard every read with a fallback instead of trusting the
  // compile-time type, since that type doesn't guarantee what the API
  // actually returned.
  const riskLocationUpdates = addressChange.risk_location_updates || [];

  const handleAddress = (data: ILocationResponsePayload) => {
    setValues({
      ...values,
      address_change: {
        ...addressChange,
        address: data.address,
        city: data.city,
        country: data.country,
        state: data.state,
        postal_code: data.postalCode,
      },
    });
  };

  const handleChangeTypeSelect = (
    _e: React.MouseEvent<HTMLElement>,
    value: (typeof ADDRESS_CHANGE_TYPE_OPTIONS)[number] | null
  ) => {
    if (!value) {
      return;
    }
    setValues(
      produce(values, (draft) => {
        draft.address_change.change_type = value;
      })
    );
  };

  const handleChangeOnCustomerScreen = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setValues(
      produce(values, (draft) => {
        draft.change_mailing_address_on_customer = e.target.value;
      })
    );
  };

  // Find (or lazily create) this risk location's row in
  // risk_location_updates.
  const getRiskLocationUpdate = (
    riskLocationNumber: number | null
  ): IHabitationalAddressChangeRiskLocationUpdate => {
    const existing = riskLocationUpdates.find(
      (item) => item.risk_location_number === riskLocationNumber
    );
    if (existing) {
      return existing;
    }
    return {
      risk_location_number: riskLocationNumber,
      selected: false,
      occupancy_type: null,
      access_type: null,
      smokers: null,
      no_of_families: null,
    };
  };

  const updateRiskLocationRow = (
    riskLocationNumber: number | null,
    patch: Partial<IHabitationalAddressChangeRiskLocationUpdate>
  ) => {
    setValues(
      produce(values, (draft) => {
        if (!draft.address_change.risk_location_updates) {
          draft.address_change.risk_location_updates = [];
        }
        const list = draft.address_change.risk_location_updates;
        const index = list.findIndex(
          (item) => item.risk_location_number === riskLocationNumber
        );
        if (index === -1) {
          list.push({
            ...getRiskLocationUpdate(riskLocationNumber),
            ...patch,
          });
        } else {
          list[index] = { ...list[index], ...patch };
        }
      })
    );
  };

  const handleRiskLocationToggle =
    (riskLocationNumber: number | null) =>
    (_e: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
      updateRiskLocationRow(riskLocationNumber, { selected: checked });
    };

  return (
    <>
      {/* "Change" toggle for the whole section, matching the mockup's header checkbox. */}
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
        <CustomCheckbox
          checked={!!addressChange.address}
          disabled
          sx={{ p: 0 }}
        />
        <Typography variant="subtitle1" fontWeight={700}>
          Change
        </Typography>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Mailing / risk location address. Moving a risk location usually
        changes its occupancy type, access type and other rating details too.
      </Typography>

      {/* Mailing only / Risk location only / Mailing and risk location */}
      <Box sx={{ mb: 2 }}>
        <ToggleButtonGroup
          value={addressChange.change_type}
          exclusive
          size="small"
          onChange={handleChangeTypeSelect}
          sx={{
            borderRadius: "50px",
            border: "1px solid",
            borderColor: "divider",
            gap: 0.25,
            p: 0.4,
            "& .MuiToggleButtonGroup-grouped": {
              border: 0,
              "&:not(:first-of-type)": { borderRadius: "50px" },
              "&:first-of-type": { borderRadius: "50px" },
            },
          }}
        >
          {ADDRESS_CHANGE_TYPE_OPTIONS.map((option) => (
            <ToggleButton
              key={option}
              value={option}
              sx={{
                px: 2,
                py: 0.6,
                fontSize: "0.85rem",
                fontWeight: 600,
                textTransform: "none",
                whiteSpace: "nowrap",
                color: "text.secondary",
                "&.Mui-selected": {
                  color: "#fff",
                  backgroundColor: "primary.main",
                  "&:hover": { backgroundColor: "primary.dark" },
                },
              }}
            >
              {option}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Box>

      {/* Change address on customer screen? */}
      <Box sx={{ mb: 2 }}>
        <CustomFormLabel sx={{ mt: 0 }}>
          Change address on customer screen? *
        </CustomFormLabel>
        <FormControl component="fieldset">
          <RadioGroup
            row
            name="change_mailing_address_on_customer"
            value={values.change_mailing_address_on_customer || ""}
            onChange={handleChangeOnCustomerScreen}
          >
            <FormControlLabel value="Yes" control={<Radio />} label="Yes" />
            <FormControlLabel value="No" control={<Radio />} label="No" />
          </RadioGroup>
        </FormControl>
      </Box>

      {/* Address row 1: address search, unit, city, province */}
      <Grid container spacing={1} sx={{ mb: 1 }}>
        <Grid item xs={12} md={5}>
          <AddressSearchAutoComplete
            address={addressChange.address}
            onAddressChange={handleAddress}
            // This panel renders in a RightPanel with zIndex 1400 (see
            // ChangeSectionDrawer); without this the "Search Address"
            // dialog opens at MUI's default modal z-index (1300) and ends
            // up stacked behind the drawer instead of on top of it.
            dialogZIndex={1500}
          />
        </Grid>
        <Grid item xs={12} md={2}>
          <CustomFormLabel>Unit</CustomFormLabel>
          <CustomTextField
            name="address_change.unit_number"
            variant="outlined"
            size="small"
            fullWidth
            value={addressChange.unit_number || ""}
            onChange={handleChange}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <CustomFormLabel>City</CustomFormLabel>
          <CustomTextField
            name="address_change.city"
            variant="outlined"
            size="small"
            fullWidth
            value={addressChange.city || ""}
            onChange={handleChange}
          />
        </Grid>
        <Grid item xs={12} md={2}>
          <CustomFormLabel>Province</CustomFormLabel>
          <ControlledCustomSelect
            variant="outlined"
            size="small"
            type="string"
            fullWidth
            displayEmpty
            name="address_change.state"
            value={addressChange.state}
            onChange={handleChange}
            placeholder="Select"
            options={provincesAndStates}
            MenuProps={SELECT_MENU_PROPS}
          />
        </Grid>
      </Grid>

      {/* Address row 2: postal code, phone, email */}
      <Grid container spacing={1} sx={{ mb: 2 }}>
        <Grid item xs={12} md={3}>
          <CustomFormLabel>Postal Code</CustomFormLabel>
          <CustomTextField
            name="address_change.postal_code"
            variant="outlined"
            size="small"
            fullWidth
            value={addressChange.postal_code || ""}
            onChange={handleChange}
          />
        </Grid>
        <Grid item xs={12} md={4.5}>
          <CustomFormLabel>Phone</CustomFormLabel>
          <CustomTextField
            name="address_change.phone"
            type="tel"
            variant="outlined"
            size="small"
            fullWidth
            value={addressChange.phone || ""}
            onChange={handleChange}
          />
        </Grid>
        <Grid item xs={12} md={4.5}>
          <CustomFormLabel>Email</CustomFormLabel>
          <CustomTextField
            name="address_change.email"
            type="email"
            variant="outlined"
            size="small"
            fullWidth
            value={addressChange.email || ""}
            onChange={handleChange}
          />
        </Grid>
      </Grid>

      {/* Active risk locations on the policy — re-rate fields per selected unit. */}
      <Typography variant="subtitle2" fontWeight={700} sx={{ mt: 1, mb: 1 }}>
        Risk Locations
      </Typography>
      <Stack spacing={1.5}>
        {activeRiskLocations.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            No active risk locations found on this policy.
          </Typography>
        )}
        {activeRiskLocations.map((riskLocation: IHabitationalRiskLocation) => {
          const update = getRiskLocationUpdate(riskLocation.incremental_number);
          const riskLocationLabel = `${
            riskLocation.incremental_number ?? "--"
          } - ${riskLocation.address || ""}`.trim();

          return (
            <Box
              key={riskLocation.incremental_number}
              sx={{
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 1,
                p: 1.5,
              }}
            >
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                <CustomCheckbox
                  checked={update.selected}
                  onChange={handleRiskLocationToggle(
                    riskLocation.incremental_number
                  )}
                  sx={{ p: 0 }}
                />
                <Typography variant="body2" fontWeight={700}>
                  {riskLocationLabel}
                </Typography>
                {riskLocation.city && (
                  <Typography variant="caption" color="text.secondary">
                    {riskLocation.city}, {riskLocation.province_or_state}
                  </Typography>
                )}
              </Stack>

              <Grid container spacing={1.5} sx={{ mb: 1 }}>
                <Grid item xs={6} md={3}>
                  <CurrentValue
                    label="Occupancy Type"
                    value={riskLocation.risk_location_construction?.occupancy_type}
                  />
                </Grid>
                <Grid item xs={6} md={3}>
                  <CurrentValue
                    label="Access Type"
                    value={riskLocation.risk_location_construction?.access_type}
                  />
                </Grid>
                <Grid item xs={6} md={3}>
                  <CurrentValue
                    label="Smokers"
                    value={riskLocation.risk_location_construction?.smokers}
                  />
                </Grid>
                <Grid item xs={6} md={3}>
                  <CurrentValue
                    label="No. of Families"
                    value={riskLocation.risk_location_construction?.no_of_families}
                  />
                </Grid>
              </Grid>

              <Grid container spacing={1.5}>
                <Grid item xs={6} md={3}>
                  <ControlledCustomSelect
                    variant="outlined"
                    size="small"
                    type="string"
                    fullWidth
                    displayEmpty
                    placeholder="No change"
                    value={update.occupancy_type || ""}
                    onChange={(e: any) =>
                      updateRiskLocationRow(riskLocation.incremental_number, {
                        occupancy_type: e.target.value,
                      })
                    }
                    options={OCCUPANCY_TYPE_OPTIONS}
                    MenuProps={SELECT_MENU_PROPS}
                  />
                </Grid>
                <Grid item xs={6} md={3}>
                  <ControlledCustomSelect
                    variant="outlined"
                    size="small"
                    type="string"
                    fullWidth
                    displayEmpty
                    placeholder="No change"
                    value={update.access_type || ""}
                    onChange={(e: any) =>
                      updateRiskLocationRow(riskLocation.incremental_number, {
                        access_type: e.target.value,
                      })
                    }
                    options={ACCESS_TYPE_OPTIONS}
                    MenuProps={SELECT_MENU_PROPS}
                  />
                </Grid>
                <Grid item xs={6} md={3}>
                  <ControlledCustomSelect
                    variant="outlined"
                    size="small"
                    type="string"
                    fullWidth
                    displayEmpty
                    placeholder="No change"
                    value={update.smokers || ""}
                    onChange={(e: any) =>
                      updateRiskLocationRow(riskLocation.incremental_number, {
                        smokers: e.target.value,
                      })
                    }
                    options={SMOKERS_OPTIONS}
                    MenuProps={SELECT_MENU_PROPS}
                  />
                </Grid>
                <Grid item xs={6} md={3}>
                  <ControlledCustomSelect
                    variant="outlined"
                    size="small"
                    type="string"
                    fullWidth
                    displayEmpty
                    placeholder="No change"
                    value={update.no_of_families || ""}
                    onChange={(e: any) =>
                      updateRiskLocationRow(riskLocation.incremental_number, {
                        no_of_families: e.target.value,
                      })
                    }
                    options={FAMILIES_OPTIONS}
                    MenuProps={SELECT_MENU_PROPS}
                  />
                </Grid>
              </Grid>
            </Box>
          );
        })}
      </Stack>
    </>
  );
};
